import { useState, useEffect, useRef } from 'react';
import { Languages } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import { useTranslation } from 'react-i18next';

interface Translation {
  [locale: string]: string;
}

interface TranslationDialogProps {
  open: boolean;
  onOpenChange: (_open: boolean) => void;
  value: string;
  translations?: Translation;
  onChange: (_translations: Translation) => void;
  fieldType?: 'input' | 'textarea';
  label: string;
  supportedLanguages?: string[];
}

const ALL_LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'pt', name: 'Português', flag: '🇵🇹' },
  { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
  { code: 'pl', name: 'Polski', flag: '🇵🇱' },
  { code: 'ja', name: '日本語', flag: '🇯🇵' },
  { code: 'ko', name: '한국어', flag: '🇰🇷' },
  { code: 'zh', name: '中文', flag: '🇨🇳' },
  { code: 'ru', name: 'Русский', flag: '🇷🇺' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦' },
  { code: 'hi', name: 'हिन्दी', flag: '🇮🇳' },
  { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
  { code: 'sv', name: 'Svenska', flag: '🇸🇪' },
  { code: 'no', name: 'Norsk', flag: '🇳🇴' },
  { code: 'da', name: 'Dansk', flag: '🇩🇰' },
  { code: 'fi', name: 'Suomi', flag: '🇫🇮' },
  { code: 'cs', name: 'Čeština', flag: '🇨🇿' },
  { code: 'hu', name: 'Magyar', flag: '🇭🇺' },
  { code: 'ro', name: 'Română', flag: '🇷🇴' },
  { code: 'bg', name: 'Български', flag: '🇧🇬' },
  { code: 'el', name: 'Ελληνικά', flag: '🇬🇷' },
  { code: 'he', name: 'עברית', flag: '🇮🇱' },
  { code: 'th', name: 'ไทย', flag: '🇹🇭' },
  { code: 'vi', name: 'Tiếng Việt', flag: '🇻🇳' },
  { code: 'id', name: 'Bahasa Indonesia', flag: '🇮🇩' },
  { code: 'ms', name: 'Bahasa Melayu', flag: '🇲🇾' },
  { code: 'uk', name: 'Українська', flag: '🇺🇦' },
];

export function TranslationDialog({ 
  open, 
  onOpenChange, 
  value, 
  translations = {}, 
  onChange, 
  fieldType = 'input',
  label,
  supportedLanguages
}: TranslationDialogProps) {
  const { t } = useTranslation();
  const [localTranslations, setLocalTranslations] = useState<Translation>({});
  const hasInitialized = useRef(false);
  
  // Filter languages based on supportedLanguages prop
  const SUPPORTED_LANGUAGES = supportedLanguages 
    ? ALL_LANGUAGES.filter(lang => supportedLanguages.includes(lang.code))
    : ALL_LANGUAGES;

  useEffect(() => {
    // Only initialize when dialog opens for the first time
    if (open && !hasInitialized.current) {
      const languages = supportedLanguages 
        ? ALL_LANGUAGES.filter(lang => supportedLanguages.includes(lang.code))
        : ALL_LANGUAGES;
        
      const initial: Translation = { en: value };
      languages.forEach(lang => {
        if (translations[lang.code]) {
          initial[lang.code] = translations[lang.code];
        } else if (lang.code === 'en') {
          initial[lang.code] = value;
        }
      });
      setLocalTranslations(initial);
      hasInitialized.current = true;
    }
    
    // Reset the initialization flag when dialog closes
    if (!open) {
      hasInitialized.current = false;
    }
  }, [open, value, translations, supportedLanguages]); // Dependencies needed for proper initialization

  const handleSave = () => {
    onChange(localTranslations);
    onOpenChange(false);
  };

  const handleTranslationChange = (locale: string, newValue: string) => {
    setLocalTranslations(prev => ({
      ...prev,
      [locale]: newValue
    }));
  };

  const getTranslationStatus = (locale: string) => {
    const translation = localTranslations[locale];
    if (!translation || translation.trim() === '') {
      return 'missing';
    }
    return 'complete';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>{t('common.translations')} - {label}</DialogTitle>
          <DialogDescription>
            {t('products.translationDescription')}
          </DialogDescription>
        </DialogHeader>
        
        <Tabs defaultValue="en" className="flex-1 overflow-hidden flex flex-col">
          <TabsList className="grid grid-cols-6 w-full">
            {SUPPORTED_LANGUAGES.slice(0, 6).map(lang => (
              <TabsTrigger 
                key={lang.code} 
                value={lang.code}
                className="flex items-center gap-1"
              >
                <span>{lang.flag}</span>
                <span className="hidden sm:inline">{lang.name}</span>
                <span className="sm:hidden">{lang.code.toUpperCase()}</span>
                {getTranslationStatus(lang.code) === 'missing' && (
                  <span className="w-2 h-2 rounded-full bg-orange-500 ml-1" />
                )}
              </TabsTrigger>
            ))}
          </TabsList>
          
          {SUPPORTED_LANGUAGES.length > 6 && (
            <TabsList className="grid grid-cols-5 w-full mt-2">
              {SUPPORTED_LANGUAGES.slice(6).map(lang => (
                <TabsTrigger 
                  key={lang.code} 
                  value={lang.code}
                  className="flex items-center gap-1"
                >
                  <span>{lang.flag}</span>
                  <span className="hidden sm:inline">{lang.name}</span>
                  <span className="sm:hidden">{lang.code.toUpperCase()}</span>
                  {getTranslationStatus(lang.code) === 'missing' && (
                    <span className="w-2 h-2 rounded-full bg-orange-500 ml-1" />
                  )}
                </TabsTrigger>
              ))}
            </TabsList>
          )}

          <div className="flex-1 overflow-y-auto mt-4">
            {SUPPORTED_LANGUAGES.map(lang => (
              <TabsContent key={lang.code} value={lang.code} className="space-y-2">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-2xl">{lang.flag}</span>
                  <Label className="text-lg font-medium">{lang.name}</Label>
                  {lang.code === 'en' && (
                    <span className="text-sm text-muted-foreground ml-2">
                      ({t('common.defaultLanguage')})
                    </span>
                  )}
                </div>
                
                {fieldType === 'textarea' ? (
                  <Textarea
                    value={localTranslations[lang.code] || ''}
                    onChange={(e) => handleTranslationChange(lang.code, e.target.value)}
                    placeholder={t('products.enterTranslation', { language: lang.name })}
                    rows={4}
                    className="w-full"
                  />
                ) : (
                  <Input
                    value={localTranslations[lang.code] || ''}
                    onChange={(e) => handleTranslationChange(lang.code, e.target.value)}
                    placeholder={t('products.enterTranslation', { language: lang.name })}
                    className="w-full"
                  />
                )}
                
                {lang.code !== 'en' && localTranslations.en && (
                  <div className="mt-2 p-3 bg-muted rounded-md">
                    <p className="text-sm text-muted-foreground">{t('common.originalText')}:</p>
                    <p className="text-sm mt-1">{localTranslations.en}</p>
                  </div>
                )}
              </TabsContent>
            ))}
          </div>
        </Tabs>
        
        <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSave}>
            {t('common.save')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface TranslationButtonProps {
  onClick: () => void;
  translationStatus?: 'complete' | 'incomplete' | 'none';
}

export function TranslationButton({ onClick, translationStatus = 'none' }: TranslationButtonProps) {
  const { t } = useTranslation();
  
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={onClick}
      className="h-8 px-2 text-muted-foreground hover:text-foreground"
      title={t('products.manageTranslations')}
    >
      <Languages className="h-4 w-4" />
      {translationStatus === 'complete' && (
        <span className="ml-1 w-2 h-2 rounded-full bg-green-500" />
      )}
      {translationStatus === 'incomplete' && (
        <span className="ml-1 w-2 h-2 rounded-full bg-red-500" />
      )}
    </Button>
  );
}
